import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import { TimeSpan } from '@/utility/time-span.ts';

interface TimerData {
	interactionId: string;
	createdAt: string;
	timeoutAt: string;
	lastSync: number;
}

const STORAGE_KEY = 'solver_timers';
const SYNC_INTERVAL = TimeSpan.seconds.one;
const CLEANUP_INTERVAL = TimeSpan.minutes.one;

const getStoredTimers = (): Record<string, TimerData> => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		return stored ? JSON.parse(stored) : {};
	} catch {
		return {};
	}
};

const saveTimers = (timers: Record<string, TimerData>): void => {
	try {
		if (Object.keys(timers).length > 0) {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(timers));
		} else {
			localStorage.removeItem(STORAGE_KEY);
		}
	} catch (error) {
		console.error('Timer kaydetme hatası:', error);
	}
};

const getTimerData = (interactionId: string): TimerData | null => {
	const timers = getStoredTimers();
	return timers[interactionId] || null;
};

const calculateRemainingSeconds = (timeoutAt: string): number => {
	const now = dayjs();
	const timeout = dayjs(timeoutAt);
	const remaining = timeout.diff(now, 'seconds');
	return Math.max(0, remaining);
};

const cleanupExpiredTimers = (): void => {
	const timers = getStoredTimers();
	const cleanedTimers: Record<string, TimerData> = {};

	Object.values(timers).forEach(timer => {
		if (calculateRemainingSeconds(timer.timeoutAt) > 0) {
			cleanedTimers[timer.interactionId] = timer;
		}
	});

	saveTimers(cleanedTimers);
};

export const useSolverTimer = (interactionId?: string) => {
	const navigate = useNavigate();
	const intervalRef = useRef<NodeJS.Timeout | null>(null);
	const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const lastInteractionIdRef = useRef<string | undefined>();

	const [remainingTime, setRemainingTime] = useState(0);
	const [isActive, setIsActive] = useState(false);

	const updateTimerForInteraction = useCallback(() => {
		if (!interactionId) {
			setRemainingTime(0);
			setIsActive(false);
			return;
		}

		const timerData = getTimerData(interactionId);
		if (!timerData) {
			setRemainingTime(0);
			setIsActive(false);
			return;
		}

		const remaining = calculateRemainingSeconds(timerData.timeoutAt);

		if (remaining <= 0) {
			const timers = getStoredTimers();
			delete timers[interactionId];
			saveTimers(timers);

			setRemainingTime(0);
			setIsActive(false);
			return;
		}

		setRemainingTime(remaining);
		setIsActive(true);
	}, [interactionId]);

	const startTimer = useCallback(
		(createdAt: string, timeoutAt: string) => {
			if (!interactionId) {
				return false;
			}

			const remaining = calculateRemainingSeconds(timeoutAt);

			if (remaining <= 0) {
				return false;
			}

			const existingTimer = getTimerData(interactionId);
			if (existingTimer) {
				const existingRemaining = calculateRemainingSeconds(existingTimer.timeoutAt);
				if (existingRemaining > 0) {
					setRemainingTime(existingRemaining);
					setIsActive(true);
					return true;
				}
			}

			const timers = getStoredTimers();
			timers[interactionId] = {
				interactionId,
				createdAt,
				timeoutAt,
				lastSync: Date.now()
			};
			saveTimers(timers);

			setRemainingTime(remaining);
			setIsActive(true);

			return true;
		},
		[interactionId]
	);

	const stopTimer = useCallback(() => {
		setIsActive(false);

		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, []);

	const clearTimer = useCallback(() => {
		if (!interactionId) return;

		stopTimer();

		const timers = getStoredTimers();
		delete timers[interactionId];
		saveTimers(timers);

		setRemainingTime(0);
	}, [interactionId, stopTimer]);

	const clearAllTimers = useCallback(() => {
		localStorage.removeItem(STORAGE_KEY);
		stopTimer();
		setRemainingTime(0);
	}, [stopTimer]);

	const getTimerInfo = useCallback(() => {
		if (!interactionId) return null;

		const timerData = getTimerData(interactionId);
		if (!timerData) return null;

		const remaining = calculateRemainingSeconds(timerData.timeoutAt);

		return {
			...timerData,
			remainingTime: remaining
		};
	}, [interactionId]);

	useEffect(() => {
		if (lastInteractionIdRef.current !== interactionId) {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}

			lastInteractionIdRef.current = interactionId;
			updateTimerForInteraction();
		}
	}, [interactionId, updateTimerForInteraction]);

	useEffect(() => {
		if (isActive && interactionId) {
			intervalRef.current = setInterval(() => {
				const timerData = getTimerData(interactionId);
				if (!timerData) {
					setIsActive(false);
					setRemainingTime(0);
					return;
				}

				const remaining = calculateRemainingSeconds(timerData.timeoutAt);

				if (remaining <= 0) {
					setIsActive(false);
					setRemainingTime(0);

					const timers = getStoredTimers();
					delete timers[interactionId];
					saveTimers(timers);
				} else {
					setRemainingTime(remaining);

					if (remaining % 10 === 0) {
						const timers = getStoredTimers();
						if (timers[interactionId]) {
							timers[interactionId].lastSync = Date.now();
							saveTimers(timers);
						}
					}
				}
			}, SYNC_INTERVAL);
		} else if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [isActive, interactionId, navigate]);

	useEffect(() => {
		cleanupIntervalRef.current = setInterval(cleanupExpiredTimers, CLEANUP_INTERVAL);

		return () => {
			if (cleanupIntervalRef.current) {
				clearInterval(cleanupIntervalRef.current);
				cleanupIntervalRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
			if (cleanupIntervalRef.current) {
				clearInterval(cleanupIntervalRef.current);
			}
		};
	}, []);

	const getActiveTimerCount = useCallback(() => {
		const timers = getStoredTimers();
		return Object.values(timers).filter(timer => calculateRemainingSeconds(timer.timeoutAt) > 0).length;
	}, []);

	return {
		remainingTime,
		isActive,

		startTimer,
		stopTimer,
		clearTimer,

		clearAllTimers,
		getTimerInfo,
		getActiveTimerCount,

		hasStoredTime: remainingTime > 0
	};
};

export const formatTime = (seconds: number): string => {
	const minutes = Math.floor(seconds / 60);
	const secs = seconds % 60;
	return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export const getAllActiveTimers = () => {
	const timers = getStoredTimers();
	return Object.values(timers)
		.map(timer => ({
			...timer,
			remainingTime: calculateRemainingSeconds(timer.timeoutAt)
		}))
		.filter(timer => timer.remainingTime > 0);
};
